import { Spinner } from '@/components/glitch-ui/spinner';
import { Suspense, useCallback, useEffect, useState, useRef } from 'react';
import { createFileRoute, useNavigate, useLocation, useRouterState } from '@tanstack/react-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { formatDistance } from 'date-fns';

import { campaignDataAtom, campaignTaskTrackingAtom, googleAdsAccountAtom, organizationDataAtom, selectedAdIdAtom, newlyCreatedAdIdAtom, selectedCampaignTabAtom } from '@/store';
import { useTabPersistence } from '@/hooks/useTabPersistence';
import { campaignQueryOptions } from '@/lib/query-options';
import { CampaignSidebar, type PlatformFilter } from '@/components/campaign/campaign-sidebar';
import { usePlatformConnections } from '@/hooks/usePlatformConnections';
import { CampaignWithTransfer } from '@/components/campaign/campaign-with-transfer';
import { CampaignReadOnlyBanner } from '@/components/campaign/campaign-read-only-banner';
import { AdvancedSection, type CampaignTabType } from '@/components/campaign/ads/advanced-section';
import { useUserProfile } from '@/contexts/user.context';
import { useTestMode } from '@/hooks/useTestMode';
import { CampaignActivityFeed } from '@/components/campaign/CampaignActivityFeed';
import { CampaignFailedBanner } from '@/components/campaign/campaign-failed-banner';
import { CampaignStatusMessages } from '@/components/campaign/campaign-status-messages';
import { AdCreateModal } from '@/components/campaign/ads/ad-create-modal';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { Button } from '@/components/ui/button';
import { SaveChangesButton } from '@/components/glitch-ui/save-changes-button';
import { Card, CardContent } from '@/components/ui/card';
import { TabCardPanel, tabButtonClass } from '@/components/glitch-ui/tab-card-panel';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { Skeleton } from '@/components/ui/skeleton';

import {
  ArrowRight,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import googleAdsLogo from '/google-ads.svg';
import metaLogo from '/meta.png';
import { FilterDropdown } from '@/components/glitch-ui/filter-dropdown';
import type { FilterDropdownState } from '@/components/glitch-ui/filter-dropdown';

import {
  useCampaignDeleteMutationOptions,
  useCampaignCopyMutation,
  useCampaignRestartMutation,
  availableCompetitorsQueryOptions,
} from '@/lib/query-options';
import { createAd } from '@/lib/apis';
import { isCampaignType } from '@/lib/utils';
import { useToast } from '@/hooks/useToast';
import { useRefetchCampaign } from '@/hooks/useRefetchCampaign';
import { useCampaignActions } from '@/hooks/useCampaignActions';
import { useDeployChanges } from '@/hooks/useDeployChanges';
import { useCampaignDraftStatus } from '@/hooks/useCampaignDraft';
import { useReverseSync } from '@/hooks/useReverseSync';
import { useGoLive } from '@/hooks/useGoLive';
import useAreAllTasksCompleted from '@/hooks/useAreAllTasksCompleted';
import useLoadingOverlay from '@/hooks/useLoadingOverlay';
import { useCampaignSidebarFilters } from '@/hooks/useCampaignSidebarFilters';
import { CAMPAIGN_STATE, CAMPAIGN_TYPE } from '@/lib/types';
import type { DriftChange } from '@/lib/types';

/**
 * Render a single Google Ads drift change as a readable line.
 * Field changes show old → new; keyword/asset changes show the affected text.
 */
function formatDriftChange(change: DriftChange): string {
  if (change.field_name) {
    return `${change.field_name}: ${String(change.old_value ?? '')} → ${String(change.new_value ?? '')}`;
  }
  const text = change.keyword_text || change.asset_text || '';
  return `${change.change_type} ${change.entity_type}: ${text}`;
}

function CampaignPageWithSuspense() {
  const { campaignslug } = Route.useParams();
  return (
    <Suspense fallback={<CampaignPageSkeleton />}>
      <CampaignPage key={campaignslug} />
    </Suspense>
  );
}

export const Route = createFileRoute('/_authenticated/_layout/campaigns/google/$campaignslug/_slug/')({
  component: CampaignPageWithSuspense,
});

// Loading skeleton that matches the dashboard layout
function CampaignPageSkeleton() {
  return (
    <div className='flex flex-col h-full'>
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3" />
      </div>
      <div className="flex-1">
        <div className="grid grid-cols-12 gap-1">
          {/* Left: Campaigns List Skeleton */}
          <div className="col-span-3">
            <div className="flex items-end justify-between">
              <Skeleton className="h-10 w-32 rounded-t-lg" />
              <Skeleton className="h-9 w-20 mb-1" />
            </div>
            <Card className="rounded-lg rounded-tl-none border bg-white -mt-px">
              <CardContent className="p-2">
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="p-2">
                      <Skeleton className="h-4 w-3/4 mb-2" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          {/* Right: Campaign Detail Skeleton */}
          <div className="col-span-9">
            <div className="flex items-end justify-between">
              <Skeleton className="h-10 w-24 rounded-t-lg" />
              <Skeleton className="h-9 w-32 mb-1" />
            </div>
            <Card className="rounded-lg rounded-tl-none border bg-white -mt-px">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <Skeleton className="h-8 w-1/3" />
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-32 w-full" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function RightPanelSkeleton() {
  return (
    <div className="col-span-9">
      <div className="flex items-end justify-between">
        <Skeleton className="h-10 w-24 rounded-t-lg" />
        <Skeleton className="h-9 w-32 mb-1" />
      </div>
      <Card className="rounded-lg rounded-tl-none border bg-white -mt-px">
        <CardContent className="p-6">
          <div className="space-y-4">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-32 w-full" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CampaignPage() {
  const { campaignslug } = Route.useParams();
  const navigate = useNavigate();
  const selectedAdId = useAtomValue(selectedAdIdAtom);
  const setSelectedAdId = useSetAtom(selectedAdIdAtom);
  // Use the query directly to ensure we always have the correct campaign data
  // This avoids stale atom data when switching between campaigns
  const { data: campaign } = useSuspenseQuery(campaignQueryOptions(campaignslug));

  // Clear the cross-navigation atom whenever the slug changes (#4268). A stale id from a previously
  // viewed campaign would otherwise trigger the auto-redirect effect below on a freshly-created
  // campaign that has no ads yet, producing a "Maximum update depth exceeded" render loop.
  useEffect(() => {
    setSelectedAdId(null);
  }, [campaignslug, setSelectedAdId]);

  // If an ad is selected, redirect to the ad page to show the correct menu
  useEffect(() => {
    if (selectedAdId && campaign) {
      // Verify the ad belongs to this campaign before redirecting
      const adBelongsToCampaign = campaign.ads?.some((ad: any) => ad.id === selectedAdId);
      if (adBelongsToCampaign) {
        navigate({ to: `/campaigns/google/${campaignslug}/ad/${selectedAdId}`, replace: true });
      }
    }
  }, [selectedAdId, campaign, campaignslug, navigate]);

  if (!campaign) {
    return <CampaignPageSkeleton />;
  }

  // Don't render content while we're about to redirect to ad page
  if (selectedAdId && campaign.ads?.some((ad: any) => ad.id === selectedAdId)) {
    return <CampaignPageSkeleton />;
  }

  return <CampaignPageContent campaign={campaign} />;
}

// Valid tab IDs for URL hash. `landing-page` (#5701) is flag-gated — see validCampaignTabs below.
const TAB_IDS: CampaignTabType[] = ['overview', 'assets', 'budget', 'conversions', 'negatives', 'targeting', 'landing-page'];

function CampaignPageContent({ campaign }: { campaign: any }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isTransitioning = useRouterState({ select: (s) => s.isLoading });
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const refetchCampaign = useRefetchCampaign(campaign.slug);
  const setSelectedAdId = useSetAtom(selectedAdIdAtom);
  const setNewlyCreatedAdId = useSetAtom(newlyCreatedAdIdAtom);
  const wiggleRef = useRef<HTMLDivElement>(null);
  useUserProfile();

  // Redirect stale bookmarks/links: ip-exclusions moved into Targeting (#4643);
  // the Assets tab's hash was renamed #misc -> #assets.
  const rawHash = location.hash?.replace('#', '');
  // The hash can nest an Assets sub-tab under the main tab: `#assets/sitelinks` (#6171). Split so the
  // main-tab persistence only sees the first segment, while the sub-tab stays linkable across refreshes.
  const [mainHashSegment, assetsSubSegment] = (rawHash ?? '').split('/');
  const redirectedHash =
    mainHashSegment === 'ip-exclusions'
      ? '#targeting'
      : mainHashSegment === 'misc'
        ? '#assets'
        : mainHashSegment
          ? `#${mainHashSegment}`
          : location.hash;
  // Landing Page tab is test-mode ONLY while the POC is unproven (#5701). Test mode is an
  // admin-menu toggle, so the tab cannot reach a customer even if `landing-pages-enabled` is
  // rolled out to their org. Off means it isn't a valid tab either, so a stale #landing-page
  // hash falls back to overview rather than showing a hidden tab. The backend keeps its own
  // `landing-pages-enabled` gate, so a demo org needs BOTH test mode and the flag.
  const landingPagesEnabled = useTestMode();
  // External campaigns only have the Overview tab (#4209).
  const validCampaignTabs = campaign?.origin === 'external'
    ? (['overview'] as CampaignTabType[])
    : (landingPagesEnabled ? TAB_IDS : TAB_IDS.filter((tab) => tab !== 'landing-page'));
  const { activeTab, handleTabChange } = useTabPersistence(
    selectedCampaignTabAtom,
    validCampaignTabs,
    'overview',
    (tabId, replace) => navigate({ to: `/campaigns/google/${campaign.slug}` as string, hash: tabId, replace }),
    redirectedHash,
  );
  // Assets sub-tab navigation: keep the `assets` main segment, swap the sub segment so the URL is
  // shareable and survives refresh (#6171). `replace` on the default so it adds no history entry.
  const navigateToAssetsSubTab = useCallback(
    (subId: string, replace = false) =>
      navigate({ to: `/campaigns/google/${campaign?.slug}` as string, hash: `assets/${subId}`, replace }),
    [navigate, campaign?.slug],
  );

  // Campaign filter state (persisted via user settings) — includes platforms now (#4446).
  const isTestMode = useTestMode();
  const { filterTypes, filterStatuses, filterPlatforms, updateFilters: updateCampaignFilters, HIDDEN_TYPES } = useCampaignSidebarFilters();
  // Derive single-value platformFilter from the multi-select set for the sidebar prop.
  // Empty or both = 'all', single = that platform.
  const platformFilter: PlatformFilter =
    filterPlatforms.size === 1 && filterPlatforms.has('google') ? 'google' :
    filterPlatforms.size === 1 && filterPlatforms.has('meta') ? 'meta' :
    'all';

  // State management
  const [isCreateAdModalOpen, setIsCreateAdModalOpen] = useState(false);
  const [prefixedUrl, setPrefixedUrl] = useState(campaign.website_url ?? '');
  // Competitor campaigns add an ad per competitor, not per landing page (#6252).
  const [selectedCompetitorId, setSelectedCompetitorId] = useState<string | null>(null);
  const isCompetitorCampaign = isCampaignType(campaign, CAMPAIGN_TYPE.COMPETITOR);
  const { data: availableCompetitors, isLoading: isLoadingCompetitors } = useQuery(
    availableCompetitorsQueryOptions(campaign.id, isCompetitorCampaign && isCreateAdModalOpen),
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyName, setCopyName] = useState('');
  // Tell-on-click validation (#5857): Copy stays enabled; clicking it marks the empty name.
  const [copyNameError, setCopyNameError] = useState<string | undefined>(undefined);
  const [isDeleting, setIsDeleting] = useState(false);
  useLoadingOverlay(isDeleting);

  // Atom state
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const organizationData = useAtomValue(organizationDataAtom);
  const setCampaignData = useSetAtom(campaignDataAtom);
  // Mutations
  const campaignDeleteMutation = useCampaignDeleteMutationOptions(campaign.id);
  const campaignCopyMutation = useCampaignCopyMutation(campaign.id);
  const { mutate: restartCampaign, isPending: isRestarting } = useCampaignRestartMutation(campaign.id);

  // Hooks
  const { actions, states } = useCampaignActions({ campaign });
  const { isLoading: isTasksLoading, areCompleted: areAllTasksCompleted } = useAreAllTasksCompleted();
  const isStillGenerating = !isTasksLoading && !areAllTasksCompleted;

  // Deploy pending changes to Google Ads — shared with the ad detail page so the deploy
  // trigger (hasPendingChanges) and action can't diverge between the two surfaces (#5583).
  const {
    hasPendingChanges,
    deploy: handleDeployChanges,
    deployDisabled,
    overBudgetTitle,
    isDeploying,
    isForeignTransitionPending,
  } = useDeployChanges(campaign, isStillGenerating);
  const { isDirty, saveChanges, isSaving, saveSuccess, driftChanges, driftModalOpen, setDriftModalOpen } = useCampaignDraftStatus(campaign);
  const { goLive, isGoingLive, blockingReason, PaymentGate: GoLivePaymentGate } = useGoLive(campaign);
  const { dispatch: dispatchReverseSync, isPending: isReverseSyncing } = useReverseSync(campaign);


  // Pause/resume feedback lives in CampaignStatusMessages (single-slot sequence, #5669);
  // it reports busy so the Resume button waits for the confirmation to finish.
  const [statusBusy, setStatusBusy] = useState(false);

  // Task tracking from atom (set by parent _slug.tsx layout)
  const taskTracking = useAtomValue(campaignTaskTrackingAtom);

  // Banner checks
  const hasNoAds = !campaign.ads || campaign.ads.length === 0;
  const isEmptyDraft = campaign.state === CAMPAIGN_STATE.DRAFT && hasNoAds &&
    (!campaign.site_links || campaign.site_links.length === 0) &&
    (!campaign.callouts || campaign.callouts.length === 0);

  const isExternal = campaign?.origin === 'external';
  const isCrossOrg = !!(organizationData?.id && campaign?.organization_id && campaign.organization_id !== organizationData.id);
  const isCrossAccount = !isCrossOrg && !!(googleAdsAccount?.googleads_account_id && campaign?.googleads_account?.googleads_account_id && campaign.googleads_account.googleads_account_id !== googleAdsAccount.googleads_account_id);
  const isArchived = campaign?.is_deleted === true;
  // Google Ads not connected (#5937): the campaign stays viewable but read-only — every
  // mutation would only die on the backend's not-connected 409 (#5936). The banner with the
  // connect CTA renders via CampaignReadOnlyBanner. Loading counts as connected so the page
  // doesn't flash read-only while the profile resolves.
  const { googleConnected, isLoading: isLoadingConnections } = usePlatformConnections();
  const isGoogleDisconnected = !isLoadingConnections && !googleConnected;
  const isReadOnly = isExternal || isCrossOrg || isCrossAccount || isArchived || isGoogleDisconnected;

  const campaignCreation = taskTracking?.campaign_creation;
  const showFailedBanner = campaignCreation && (
    campaignCreation.failed > 0 ||
    (campaignCreation.stuck_in_progress ?? 0) > 0
  ) && !(campaignCreation.in_progress > 0 && (campaignCreation.restart_attempt_count ?? 0) > 0);

  // Failed-generation locks out editing actions across the campaign detail surfaces.
  // The CampaignApprovalFlow still renders so its failed banner is visible — only mutations are disabled.
  const hasGenerationFailed = !!showFailedBanner;
  const isMutationDisabled = isReadOnly || hasGenerationFailed;

  const showAnyBanner = !isReadOnly && (
    // Banner shows whenever generation is in flight (and hasn't failed) — drops the prior
    // `!isTasksLoading && in_progress > 0` gate that left a blank body during the task-refetch
    // and pre-task-discovery race windows. See #4354.
    ((isStillGenerating && !hasGenerationFailed) ||
    hasGenerationFailed ||
    (!isStillGenerating && hasNoAds && !isEmptyDraft) ||
    campaign.state === CAMPAIGN_STATE.ERROR ||
    (campaign.state === CAMPAIGN_STATE.PAUSED && campaign.first_published_at) || hasPendingChanges)
  );

  // Effects
  useEffect(() => {
    if (areAllTasksCompleted && campaign) {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.slug] });
    }
  }, [areAllTasksCompleted, campaign?.slug, queryClient]);

  // Refetch campaign data when generation completes to get latest ads/state
  const prevGenerating = useRef(isStillGenerating);
  useEffect(() => {
    if (prevGenerating.current && !isStillGenerating) {
      refetchCampaign();
    }
    prevGenerating.current = isStillGenerating;
  }, [isStillGenerating, refetchCampaign]);

  // Handlers
  const handleRestartCampaign = () => {
    restartCampaign(undefined, {
      onSuccess: (data) => {
        toast({
          description: `Campaign restart initiated (Attempt ${data.restart_attempt} of ${data.max_attempts})`,
        });
        queryClient.invalidateQueries({ queryKey: ['campaignTaskTracking'] });
      },
      onError: (error) => {
        toast({
          description: `Failed to restart campaign: ${error instanceof Error ? error.message : 'Unknown error'}`,
          variant: 'destructive',
        });
      },
    });
  };


  const handleDeleteCampaign = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (campaign && campaignDeleteMutation) {
      setShowDeleteConfirm(false);
      setIsDeleting(true);
      try {
        // Remove campaign queries from cache BEFORE the mutation's onSuccess fires,
        // preventing _slug.tsx's useSuspenseQuery from re-fetching a deleted campaign
        queryClient.removeQueries({ queryKey: ['campaign', campaign.slug] });
        queryClient.removeQueries({ queryKey: ['campaign-task-tracking', campaign.id] });
        setCampaignData(null);
        await Promise.all([
          campaignDeleteMutation.mutateAsync(),
          new Promise((resolve) => setTimeout(resolve, 2000)),
        ]);
        navigate({ to: '/campaigns', replace: true });
      } catch {
        setIsDeleting(false);
        toast({ description: 'Failed to delete campaign', variant: 'destructive' });
      }
    } else {
      setShowDeleteConfirm(false);
    }
  };

  const handleCopyCampaign = () => {
    setCopyName(`Copy of ${campaign.name}`);
    setCopyNameError(undefined);
    setShowCopyModal(true);
  };

  const handleConfirmCopy = async () => {
    if (!copyName.trim()) {
      setCopyNameError('Required');
      return;
    }
    if (copyName.length > 256) {
      toast({
        description: 'Campaign name must be 256 characters or less',
        variant: 'destructive'
      });
      return;
    }

    try {
      const copiedCampaign = await campaignCopyMutation.mutateAsync(copyName.trim() || undefined);
      setShowCopyModal(false);
      setCopyName('');
      toast({ description: 'Campaign copied successfully!' });
      navigate({ to: `/campaigns/google/${copiedCampaign.slug}` });
    } catch (error: any) {
      toast({
        description: error.message || 'Failed to copy campaign',
        variant: 'destructive'
      });
    }
  };

  const handleCreate = async () => {
    try {
      const newAd = await createAd(campaign.id, prefixedUrl, selectedCompetitorId ?? undefined);
      setNewlyCreatedAdId(newAd.id);
      setSelectedAdId(newAd.id);
      // Pull the new ad into campaign.ads before navigating, so it appears in the left
      // list and the selection highlights it (otherwise selectedAdId points at an ad not
      // yet in the list → nothing selected).
      await refetchCampaign();
      navigate({ to: `/campaigns/google/${campaign.slug}/ad/${newAd.id}` });
    } catch (err: any) {
      toast({
        description: err?.message || 'Failed to create Ad',
        variant: 'destructive',
      });
      throw err;
    }
  };

  return (
    <div className='flex flex-col h-full'>
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3" />
      </div>

      <div className="flex-1">
        <GoLivePaymentGate />
        <div className="grid grid-cols-12 gap-1">
          {/* Left: Campaigns List Tab Card (25%) */}
          <div className="col-span-3 sticky top-0 self-start h-[calc(100vh-120px)]">
            <TabCardPanel
              tabs={[
                { id: 'all', label: 'Campaigns', isActive: true },
                { id: 'new', label: '+ Add', onClick: () => navigate({ to: '/new/campaign' }) },
              ]}
              actions={
                <FilterDropdown
                  sections={[
                    // #6257: the Platform section is test-mode-only until Meta App Review clears —
                    // outside it the sidebar is Google-only and the filter would only confuse.
                    ...(isTestMode ? [{
                      id: 'platforms',
                      title: 'Platform',
                      options: [
                        { value: 'google', label: 'Google', icon: <img src={googleAdsLogo} alt="" className="h-4 w-4" /> },
                        { value: 'meta', label: 'Meta', icon: <img src={metaLogo} alt="" className="w-5 h-auto" /> },
                      ],
                    }] : []),
                    { id: 'statuses', title: 'Status', options: [
                      { value: 'live', label: 'Live' },
                      { value: 'draft', label: 'Draft' },
                      { value: 'paused', label: 'Paused' },
                      { value: 'archived', label: 'Archived' },
                    ]},
                    { id: 'types', title: 'Type', options: [
                      { value: 'brand', label: 'Brand' },
                      { value: 'prospecting', label: 'Prospecting' },
                      { value: 'competitor', label: 'Competitor' },
                      { value: 'hipo', label: 'High Performance' },
                      { value: 'icp', label: 'ICP' },
                      { value: 'location', label: 'Location' },
                      { value: 'external', label: 'External' },
                      { value: 'retargeting', label: 'Retargeting' },
                    ]},
                  ]}
                  selectedValues={{
                    statuses: [...filterStatuses],
                    types: [...filterTypes].filter(t => !HIDDEN_TYPES.includes(t)),
                    platforms: [...filterPlatforms],
                  }}
                  hiddenValues={{ types: HIDDEN_TYPES }}
                  showDateRange={false}
                  popoverWidth="w-[220px]"
                  onApply={(filters: FilterDropdownState) => {
                    updateCampaignFilters(
                      new Set(filters.selections.types || []),
                      new Set(filters.selections.statuses || []),
                      // When test mode is off the dropdown has no platforms section, so preserve
                      // the existing filterPlatforms (don't clobber to empty).
                      isTestMode ? new Set(filters.selections.platforms || []) : filterPlatforms,
                    );
                  }}
                />
              }
              cardClassName="overflow-hidden"
              noPadding
            >
              <CampaignSidebar filterTypes={filterTypes} filterStatuses={filterStatuses} platformFilter={platformFilter} />
            </TabCardPanel>
          </div>

          {/* Right: Campaign Detail Tab Card (75%) */}
          {isTransitioning ? <RightPanelSkeleton /> : <div className="col-span-9">
            <div className="flex items-end justify-between">
              <div className="flex items-end gap-1">
                {([
                  { id: 'overview', label: 'Overview' },
                  ...(!isExternal ? [
                    { id: 'assets', label: 'Assets' },
                    { id: 'targeting', label: 'Targeting' },
                    { id: 'budget', label: 'Budget' },
                    { id: 'conversions', label: 'Conversions' },
                    { id: 'negatives', label: 'Negatives' },
                  ] : []),
                  ...((!isExternal && landingPagesEnabled) ? [{ id: 'landing-page', label: 'Landing Page' }] : []),
                ] as { id: CampaignTabType; label: string }[]).map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`${tabButtonClass(activeTab === tab.id)} cursor-pointer`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 pb-1">
                <CampaignStatusMessages
                  firstPublishedAt={campaign.first_published_at}
                  state={campaign.state}
                  isLaunching={isGoingLive}
                  transition={states.toggleTransition}
                  onBusyChange={setStatusBusy}
                />
                {/* The button owns the Saved confirmation: it renders "Saved ✓" in place of itself,
                    never beside it (#5830 / #5347). */}
                {((!isMutationDisabled && isDirty) || (saveSuccess && !isDirty)) && (
                  <SaveChangesButton
                    onClick={saveChanges}
                    saving={isSaving}
                    saved={saveSuccess && !isDirty}
                    className="h-9 shadow-lg animate-bounce-in-right"
                  />
                )}
                {/* `|| isGoingLive` keeps the button mounted as "Going Live..." for the whole
                    launch: the state service commits LIVE + first_published_at at task start,
                    which would otherwise unmount the button mid-launch (#5663). */}
                {!isReadOnly && !isMutationDisabled && !isDirty &&
                  ((!isStillGenerating && !campaign.first_published_at &&
                    (campaign.state === CAMPAIGN_STATE.DRAFT || campaign.state === CAMPAIGN_STATE.PAUSED)) ||
                    isGoingLive) && (
                  <Button
                    onClick={goLive}
                    disabled={isGoingLive}
                    title={blockingReason ?? undefined}
                    className="h-9 bg-green-600 hover:bg-green-700 text-white shadow-lg animate-bounce-in-right"
                  >
                    {isGoingLive ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Going Live...
                      </>
                    ) : (
                      <>
                        Go Live
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </>
                    )}
                  </Button>
                )}
                {/* Deploy changes: a campaign with pending changes not yet pushed to Google Ads —
                    a LIVE campaign with a new ad / budget / settings / assets in draft, or a
                    previously-published campaign back in DRAFT. It has no Save Changes (not locally
                    dirty) and no Go Live. Reuses handleDeployChanges (approve all + re-sync), which
                    pushes the new ad and clears the draft "!" state. Same trigger as the former
                    "Campaign Changes Pending" banner (hasPendingChanges), now consolidated here. */}
                {/* Deploy stays visible while generating (#5637) — clicking surfaces a
                    "still generating" toast from useDeployChanges instead of hiding it. */}
                {/* !isForeignTransitionPending: during a first go-live the campaign transiently
                    satisfies hasPendingChanges, which rendered Deploy beside "Going Live..." (#5825). */}
                {!isReadOnly && !isMutationDisabled && !isDirty && ((hasPendingChanges && !isForeignTransitionPending) || isDeploying) && (
                  <Button
                    onClick={handleDeployChanges}
                    disabled={deployDisabled}
                    title={overBudgetTitle}
                    className="h-9 bg-green-600 hover:bg-green-700 text-white shadow-lg animate-bounce-in-right"
                  >
                    {isDeploying ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Deploying...
                      </>
                    ) : (
                      <>
                        Deploy
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </>
                    )}
                  </Button>
                )}
                {/* Sequential with the status slot (#5669): hidden while the toggle
                    transition spinner or a confirmation is showing, so exactly one
                    element occupies this spot at a time. */}
                {!isReadOnly && !isMutationDisabled && !isStillGenerating && !isDirty &&
                  campaign.state === CAMPAIGN_STATE.PAUSED && campaign.first_published_at &&
                  !states.isTogglePending && !statusBusy && (
                  <Button
                    onClick={actions.handleTogglePause}
                    disabled={states.isLoading}
                    className="h-9 bg-green-600 hover:bg-green-700 text-white shadow-lg animate-bounce-in-right"
                  >
                    Resume
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
                <CampaignWithTransfer
                  campaign={campaign}
                  variant='header'
                  onCopy={handleCopyCampaign}
                  onDelete={handleDeleteCampaign}
                  onNewAd={!isReadOnly ? () => setIsCreateAdModalOpen(true) : undefined}
                  newAdDisabled={isStillGenerating}
                />
              </div>
            </div>
            <Card className="rounded-lg rounded-tl-none border bg-white -mt-px">
              <CardContent className="p-6">
                {isReadOnly && <CampaignReadOnlyBanner campaign={campaign} />}
                <div className={showAnyBanner ? "space-y-4 mb-4" : ""}>
                  {/* Campaign Failed / Stuck Banner */}
                  {!isReadOnly && showFailedBanner && taskTracking && (
                    <CampaignFailedBanner
                      campaignCreationSummary={taskTracking.campaign_creation}
                      errorMessage={taskTracking.campaign_creation.latest_error_message}
                      onRestart={handleRestartCampaign}
                      isRestarting={isRestarting}
                      restartAttempt={taskTracking.campaign_creation.restart_attempt_count || 0}
                      maxAttempts={3}
                    />
                  )}
                  {/* Campaign Still Generating Warning — gated on `isStillGenerating` so the
                      banner is the single render target while generation is in flight (matches
                      the AdvancedSection gate at line 844). Previously gated on
                      `!isTasksLoading && in_progress > 0`, which left the body blank during the
                      task-refetch and pre-task-discovery race windows. See #4354. */}
                  {!isReadOnly && isStillGenerating && !hasGenerationFailed && (
                    <div className="bg-[#EDE9FE] border-l-4 border-l-[#290BD9] rounded-lg p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-white rounded-lg shadow p-3">
                          <Spinner size="lg" className="text-[#290BD9]" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating Campaign</h3>
                          <p className="text-gray-900 mb-3">
                            Your campaign is currently being generated. This can take 5-10 minutes.
                          </p>

                          {/* Progress Details */}
                          {taskTracking && (() => {
                            const tasks = ['ad_generation', 'keywords_generation', 'headline_generation', 'descriptions_generation', 'site_link_generation'] as const;
                            let sumTotal = 0;
                            let sumCompleted = 0;

                            tasks.forEach(taskKey => {
                              const task = taskTracking[taskKey];
                              if (task && typeof task === 'object' && 'total' in task && 'completed' in task) {
                                sumTotal += task.total;
                                sumCompleted += task.completed;
                              }
                            });

                            return (
                              <div className="mt-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    {/* Overall Progress Bar and Percentage */}
                                    <div>
                                      <div className="w-3/5 bg-gray-200 rounded-full h-2">
                                        <div
                                          className="bg-[#290BD9] h-2 rounded-full transition-all duration-300"
                                          style={{ width: `${sumTotal > 0 ? (sumCompleted / sumTotal) * 100 : 0}%` }}
                                        />
                                      </div>
                                      <p className="text-sm text-gray-700 mt-1">
                                        {sumTotal > 0 ? Math.round((sumCompleted / sumTotal) * 100) : 0}% complete
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                {/* Progress Details Section */}
                                <div className="mt-3 space-y-2">
                                  {taskTracking.ad_generation && taskTracking.ad_generation.total > 0 && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-900">Ads</span>
                                      <span className="text-gray-900">
                                        {taskTracking.ad_generation.completed}/{taskTracking.ad_generation.total}
                                        {taskTracking.ad_generation.in_progress > 0 &&
                                          <span className="text-[#290BD9] ml-1">(+{taskTracking.ad_generation.in_progress} in progress)</span>
                                        }
                                      </span>
                                    </div>
                                  )}
                                  {taskTracking.keywords_generation && taskTracking.keywords_generation.total > 0 && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-900">Keywords</span>
                                      <span className="text-gray-900">
                                        {taskTracking.keywords_generation.completed}/{taskTracking.keywords_generation.total}
                                        {taskTracking.keywords_generation.in_progress > 0 &&
                                          <span className="text-[#290BD9] ml-1">(+{taskTracking.keywords_generation.in_progress} in progress)</span>
                                        }
                                      </span>
                                    </div>
                                  )}
                                  {taskTracking.headline_generation && taskTracking.headline_generation.total > 0 && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-900">Headlines</span>
                                      <span className="text-gray-900">
                                        {taskTracking.headline_generation.completed}/{taskTracking.headline_generation.total}
                                        {taskTracking.headline_generation.in_progress > 0 &&
                                          <span className="text-[#290BD9] ml-1">(+{taskTracking.headline_generation.in_progress} in progress)</span>
                                        }
                                      </span>
                                    </div>
                                  )}
                                  {taskTracking.descriptions_generation && taskTracking.descriptions_generation.total > 0 && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-900">Descriptions</span>
                                      <span className="text-gray-900">
                                        {taskTracking.descriptions_generation.completed}/{taskTracking.descriptions_generation.total}
                                        {taskTracking.descriptions_generation.in_progress > 0 &&
                                          <span className="text-[#290BD9] ml-1">(+{taskTracking.descriptions_generation.in_progress} in progress)</span>
                                        }
                                      </span>
                                    </div>
                                  )}
                                  {taskTracking.site_link_generation && taskTracking.site_link_generation.total > 0 && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-900">Sitelinks</span>
                                      <span className="text-gray-900">
                                        {taskTracking.site_link_generation.completed}/{taskTracking.site_link_generation.total}
                                        {taskTracking.site_link_generation.in_progress > 0 &&
                                          <span className="text-[#290BD9] ml-1">(+{taskTracking.site_link_generation.in_progress} in progress)</span>
                                        }
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  )}

                  {!isReadOnly
                    && !isTasksLoading
                    && (taskTracking?.campaign_creation?.in_progress ?? 0) > 0 && (
                      <CampaignActivityFeed campaignId={campaign.id} className="mt-4" />
                    )}


                  {/* Error Card for Missing Ads. !isTasksLoading matters: isStillGenerating is
                      false while the tracking query is still in flight (it requires !isTasksLoading),
                      so without it this card flashed on every freshly created campaign until the
                      first tracking poll resolved and the generating UI took over (#5914). */}
                  {!isReadOnly && !isTasksLoading && !isStillGenerating && hasNoAds && !isEmptyDraft && (
                    <div className="bg-red-50 border-l-4 border-l-red-400 border-t border-r border-b border-red-400 rounded-lg p-6 mb-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-white rounded-lg shadow p-3">
                          <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Ads</h3>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-gray-900">
                                This campaign has 0 ads. You must create at least one ad before proceeding.
                              </p>
                            </div>
                            <Button
                              size="lg"
                              onClick={() => setIsCreateAdModalOpen(true)}
                              className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#290BD9]/90 text-white shadow-lg ml-6"
                            >
                              <span>CREATE AD</span>
                              <ArrowRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Error State */}
                  {!isReadOnly && !isTasksLoading && campaign.state === CAMPAIGN_STATE.ERROR && (
                    <div className="bg-red-50 border-l-4 border-l-red-400 rounded-lg p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-white rounded-lg shadow p-3">
                          <AlertCircle className="h-6 w-6 text-red-500" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">Campaign Creation Failed</h3>
                          <div className="flex items-start justify-between">
                            <p className="text-gray-900 flex-1">
                              Please delete this campaign and create a new one.
                            </p>
                            <Button
                              size="lg"
                              onClick={handleDeleteCampaign}
                              disabled={campaignDeleteMutation.isPending}
                              className="w-[280px] py-6 bg-red-600 hover:bg-red-600/90 text-white shadow-lg ml-6"
                            >
                              <span>DELETE CAMPAIGN</span>
                              <ArrowRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Advanced Section Content — render once generation has finished
                    OR failed, so failed campaigns are still inspectable read-only. */}
                {(!isStillGenerating || hasGenerationFailed) && campaign && (
                  <div ref={wiggleRef}>
                    <AdvancedSection
                      campaign={campaign}
                      activeTab={activeTab}
                      isExternal={isMutationDisabled}
                      assetsSubTab={assetsSubSegment}
                      onAssetsSubTabChange={navigateToAssetsSubTab}
                    />
                  </div>
                )}

                {/* #4799: sync pulls from the Google Ads counterpart, which only exists once the
                    campaign has been published at least once — disabled (not hidden) in draft. */}
                {(!isStillGenerating || hasGenerationFailed) && !isReadOnly && activeTab === 'overview' && (
                  <div className="mt-6 flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => dispatchReverseSync()}
                      disabled={isReverseSyncing || !campaign.first_published_at}
                      title={
                        !campaign.first_published_at
                          ? 'Available once the campaign has been live on Google Ads'
                          : campaign.last_reverse_synced_at
                            ? `Synced ${formatDistance(new Date(campaign.last_reverse_synced_at), new Date(), { addSuffix: true })}`
                            : undefined
                      }
                      className="h-9 text-gray-900"
                    >
                      {isReverseSyncing ? (
                        <Spinner size="sm" className="mr-2" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      Sync from Google Ads
                    </Button>
                    {campaign.last_reverse_synced_at && !isReverseSyncing && (
                      <span className="text-xs text-gray-500">
                        Synced {formatDistance(new Date(campaign.last_reverse_synced_at), new Date(), { addSuffix: true })}
                      </span>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>}
        </div>
      </div>

      {/* Modals */}
      {campaign && campaign.website_url && (
        <AdCreateModal
          isOpen={isCreateAdModalOpen}
          onClose={() => setIsCreateAdModalOpen(false)}
          campaignUrl={campaign.website_url}
          setPrefixedUrl={setPrefixedUrl}
          handleCreateAd={handleCreate}
          isCompetitorCampaign={isCompetitorCampaign}
          competitors={availableCompetitors ?? []}
          isLoadingCompetitors={isLoadingCompetitors}
          setSelectedCompetitorId={setSelectedCompetitorId}
        />
      )}

      <ConformationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Campaign"
        description={`Are you sure you want to delete "${campaign?.name}"? This action cannot be undone.`}
        confirmText="Delete Campaign"
        cancelText="Cancel"
        isLoading={campaignDeleteMutation?.isPending}
      />

      <Dialog open={showCopyModal} onOpenChange={setShowCopyModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Copy Campaign</DialogTitle>
            <DialogDescription>
              Create a copy of "{campaign.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="campaign-name" className="text-gray-900">Campaign Name</Label>
                <FieldError message={copyNameError} />
              </div>
              <div className="relative">
                <Input
                  id="campaign-name"
                  value={copyName}
                  onChange={(e) => {
                    setCopyName(e.target.value);
                    setCopyNameError(undefined);
                  }}
                  maxLength={256}
                  placeholder="Enter campaign name"
                  error={!!copyNameError}
                  className={copyNameError ? 'w-full pr-8' : 'w-full'}
                />
                <InputAdornment error={!!copyNameError} />
              </div>
              <p className="text-sm text-gray-500">
                {copyName.length}/256 characters
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowCopyModal(false);
                setCopyName('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmCopy}
              disabled={campaignCopyMutation.isPending}
            >
              {campaignCopyMutation.isPending ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Copying...
                </>
              ) : (
                'Copy Campaign'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Drift guard: Google Ads changed since this campaign was loaded (PUT returned 409). The
          change_event detector is existence-only (#5566 Req 2), so `changes` may be empty — show the
          message and let the user pull via the existing reverse-sync, then re-apply their edit. */}
      <Dialog open={driftModalOpen} onOpenChange={setDriftModalOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Changed in Google Ads</DialogTitle>
            <DialogDescription>
              This campaign was edited in Google Ads since it was loaded. Sync from Google Ads to pull those
              changes in, then re-apply your edit.
            </DialogDescription>
          </DialogHeader>
          {driftChanges.length > 0 && (
            <div className="py-2 space-y-2">
              {driftChanges.map((change, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm text-gray-900">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-[#A16207]" />
                  <span>{formatDriftChange(change)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDriftModalOpen(false)} disabled={isReverseSyncing}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                await dispatchReverseSync();
                setDriftModalOpen(false);
              }}
              disabled={isReverseSyncing}
              className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
            >
              {isReverseSyncing ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync from Google Ads
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
